// MissionRow: Individual mission list item for the agenda view
// Desktop: CSS grid layout (title | operator | service | time | recurrence) with left accent bar
// Mobile: two-line stacked layout with left accent bar
// CREATED: Part of the agenda list view for TeamView
// UPDATED: Removed redundant color dot (accent bar retained), CSS grid desktop layout,
//          improved time/duration hierarchy, wider operator column, focus-visible rings
// UPDATED: Increased time/duration column from 96px to 120px to prevent wrapping

import { Clock, User, CheckCircle2, AlertCircle } from 'lucide-react';
import { getServiceColorClass, getServiceAccentHex } from '../../../lib/services';
import type { Service } from '../../../lib/services';
import { RecurrenceBadge } from './RecurrenceBadge';
import type { EnrichedMission } from './types';
import type { TaskStatus } from '../../../lib/statusUtils';

interface MissionRowProps {
  task: EnrichedMission;
  services: Service[];
  onClick: () => void;
  isDesktop: boolean;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}`;
}

function formatTime(time: string | null): string | null {
  if (!time) return null;
  return time.slice(0, 5);
}

function StatusIcon({ status }: { status: string | null }) {
  if (status === 'completed') {
    return <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" aria-label="Terminée" />;
  }
  if (status === 'overdue') {
    return <AlertCircle size={14} className="text-red-500 flex-shrink-0" aria-label="En retard" />;
  }
  return null;
}

function StatusBadgeMobile({ status }: { status: string | null }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600">
        <CheckCircle2 size={10} />
        Terminée
      </span>
    );
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600">
        <AlertCircle size={10} />
        En retard
      </span>
    );
  }
  return null;
}

export function MissionRow({ task, services, onClick, isDesktop }: MissionRowProps) {
  const service = services.find(s => s.id === task.template_service_id);
  const operatorName = task.assigned_to_user?.full_name || task.user?.full_name || '—';
  const title = task.template.name;
  const duration = task.duration_override_minutes ?? task.template.estimated_duration_minutes;
  const time = formatTime(task.scheduled_time);
  const serviceColorClass = service?.color ? getServiceColorClass(service.color) : '';
  const accentColor = service?.color ? getServiceAccentHex(service.color) : '#e5e7eb';
  const status = task.status as TaskStatus | null;

  if (isDesktop) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-stretch text-left hover:bg-primary-50/40 active:bg-primary-50/60 transition-colors duration-150 border-b border-gray-100 last:border-b-0 focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-primary-400 group"
      >
        <span
          className="w-1 self-stretch flex-shrink-0 rounded-sm"
          style={{ backgroundColor: accentColor }}
          aria-hidden="true"
        />

        <div
          className="grid items-center gap-x-4 flex-1 px-4 py-2.5"
          style={{ gridTemplateColumns: '1fr 170px 100px 120px 90px' }}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="truncate text-sm font-medium text-gray-900 group-hover:text-primary-700 transition-colors duration-150">
              {title}
            </span>
            <StatusIcon status={status} />
          </span>

          <span className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
            <User size={11} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{operatorName}</span>
          </span>

          <span className="hidden lg:flex items-center">
            {service ? (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${serviceColorClass}`}>
                {service.name}
              </span>
            ) : null}
          </span>

          <span className="flex items-center gap-1 text-xs justify-end">
            {time && (
              <>
                <Clock size={11} className="text-gray-400 flex-shrink-0" />
                <span className="text-gray-700 font-medium tabular-nums">{time}</span>
                <span className="text-gray-300">·</span>
              </>
            )}
            <span className="text-gray-400 tabular-nums">{formatDuration(duration)}</span>
          </span>

          <span className="flex items-center justify-end">
            <RecurrenceBadge
              frequency={task.recurrenceFrequency}
              interval={task.recurrenceInterval}
            />
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-stretch text-left active:bg-primary-50/40 transition-colors duration-150 border-b border-gray-100 last:border-b-0 focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-primary-400"
    >
      <span
        className="w-1 flex-shrink-0 rounded-sm"
        style={{ backgroundColor: accentColor }}
        aria-hidden="true"
      />

      <div className="flex flex-col gap-1 px-3 py-3 flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className="flex-1 text-sm font-medium text-gray-900 leading-snug">
            {title}
          </span>
          <RecurrenceBadge
            frequency={task.recurrenceFrequency}
            interval={task.recurrenceInterval}
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <span className="truncate max-w-[160px]">{operatorName}</span>
          {service && (
            <>
              <span className="text-gray-300">·</span>
              <span>{service.name}</span>
            </>
          )}
          {time && (
            <>
              <span className="text-gray-300">·</span>
              <Clock size={10} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-700 font-medium">{time}</span>
            </>
          )}
          <span className="text-gray-400">{formatDuration(duration)}</span>
          {status && status !== 'not_started' && (
            <>
              <span className="text-gray-300">·</span>
              <StatusBadgeMobile status={status} />
            </>
          )}
        </div>
      </div>
    </button>
  );
}
